import { useState } from 'react';
import TagEditor from '../TagEditor';
import TagFilter from '../TagFilter';

export default function NoteList({ 
  notes, 
  filteredNotes, 
  setSelectedNoteId, 
  noteSearchText, 
  setNoteSearchText, 
  globalSelectedTags,
  setGlobalSelectedTags,
  selectedNoteFilter,
  setSelectedNoteFilter,
  allAvailableTags,
  notesForSelectedTag, 
  handleExtractInsightsByTag, 
  isCrystallizing,
  insights,
  isLargeScreen,
  handleReSummarize,
  isSummarizing,
  handleDiscussInSink,
  handleUpdateNoteTags,
  tagSuggestions = [],
  showToast,
}) {
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const filters = (
    <div className="space-y-3">
      <TagFilter
        globalSelectedTags={globalSelectedTags}
        setGlobalSelectedTags={setGlobalSelectedTags}
        allAvailableTags={allAvailableTags}
        themeColor="blue"
      />
      {globalSelectedTags.length > 0 && (
        <div className="rounded-xl border border-purple-500/30 bg-purple-950/20 p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold text-purple-200">{globalSelectedTags.map(t => '#' + t).join(', ')} からHint抽出</p>
              <p className="text-[10px] text-gray-400 mt-1">
                横断して、共通パターン・矛盾を拾います。
              </p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={handleExtractInsightsByTag} disabled={isCrystallizing || notesForSelectedTag.length === 0} className="flex-shrink-0 rounded-lg bg-purple-600 px-3 py-1.5 text-[10px] font-bold text-white hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500">
                {isCrystallizing ? '抽出中' : '抽出'}
              </button>
              {handleDiscussInSink && (
                <button
                  onClick={() => handleDiscussInSink('tag', globalSelectedTags.join(','), globalSelectedTags.join(', '))}
                  className="flex-shrink-0 rounded-lg bg-indigo-600 px-3 py-1 text-[10px] font-bold text-white hover:bg-indigo-500"
                >
                  Sinkで議論
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const bottomControls = (
    <div className="shrink-0 px-4 py-3 border-t border-gray-800 bg-gray-900 shadow-2xl space-y-3">
      {filters}
      <div className="relative w-full">
        <input
          type="text"
          value={noteSearchText}
          onChange={(e) => setNoteSearchText(e.target.value)}
          placeholder="メモを検索..."
          className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-xl px-4 py-2 pl-10 outline-none focus:border-blue-500 transition-all placeholder-gray-500 shadow-inner h-[40px]"
        />
        <svg className="absolute left-3.5 top-3 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {noteSearchText && (
          <button aria-label="検索をクリア" onClick={() => setNoteSearchText('')} className="absolute right-3 top-3 text-gray-500 hover:text-gray-300">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  const activeFilters = (selectedNoteFilter !== 'all' || globalSelectedTags.length > 0) && (
    <div className="flex flex-wrap gap-2 mb-4">
      {selectedNoteFilter !== 'all' && (
        <span className="flex items-center gap-1 bg-blue-950/40 text-blue-300 border border-blue-800/50 px-2 py-1 rounded-lg text-[10px] font-bold">
          状態: {{'unextracted':'Hint未抽出','extracted':'Hint抽出済'}[selectedNoteFilter]}
          <button onClick={() => setSelectedNoteFilter('all')} className="hover:text-white transition-colors" aria-label="状態フィルタを解除">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </span>
      )}
      {globalSelectedTags.map(tag => (
        <span key={tag} className="flex items-center gap-1 bg-gray-800 text-gray-300 border border-gray-700 px-2 py-1 rounded-lg text-[10px] font-bold">
          #{tag}
          <button onClick={() => setGlobalSelectedTags(globalSelectedTags.filter(t => t !== tag))} className="hover:text-white transition-colors" aria-label={`${tag}タグフィルタを解除`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </span>
      ))}
    </div>
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-gray-900">
      <div className={`flex-1 overflow-y-auto overscroll-contain ${isLargeScreen ? 'px-4 py-4' : 'p-4 space-y-3'}`}>
        {activeFilters}
        <div className="space-y-3">
          {filteredNotes.length === 0 && (
            <div className="text-sm text-gray-500 border border-gray-800 rounded-lg p-4">
              {notes.length === 0 ? 'まとめたNoteはまだありません。' : '条件に合うNoteはありません。'}
            </div>
          )}
          {filteredNotes.map((note) => (
            <div key={note.id} className="relative">
              <button onClick={() => setSelectedNoteId(note.id)} className="w-full text-left bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg p-4 group transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-white line-clamp-2">{note.title || 'Untitled'}</h3>
                      {insights && insights.some(insight => (insight.sourceNoteIds || []).includes(note.id)) && (
                        <span className="inline-flex flex-shrink-0 text-[9px] font-bold text-purple-300 bg-purple-900/40 border border-purple-700/50 rounded px-1.5 py-0.5 whitespace-nowrap">
                          Hint抽出済
                        </span>
                      )}
                    </div>
                    {/* Legacy: sourceMode === 'buddy' is kept for backward compatibility with old notes */}
                    {note.metadata?.sourceMode === 'buddy' && (
                      <span className="inline-block w-fit text-[9px] font-bold text-indigo-300 bg-indigo-950/40 border border-indigo-800/50 rounded px-1.5 py-0.5">Buddy由来</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedNoteId(expandedNoteId === note.id ? null : note.id);
                      }}
                      className={`p-1.5 rounded-md border transition-all ${expandedNoteId === note.id ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-900 border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}
                      title="タグを編集"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </button>
                    {note.metadata?.summaryCompleteness === 'partial' && (
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-300 bg-amber-950/40 border border-amber-700/50 rounded px-1.5 py-0.5 whitespace-nowrap">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                          まとめが途切れています
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReSummarize?.(note);
                          }}
                          disabled={isSummarizing}
                          className="text-[10px] font-bold bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 text-white px-2.5 py-1 rounded shadow-sm transition-colors flex items-center gap-1.5"
                        >
                          {isSummarizing && (
                            <svg className="animate-spin h-3 w-3 text-white" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                          {isSummarizing ? '再まとめ中' : '再まとめ'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-[13px] text-gray-400 mt-2 line-clamp-3 whitespace-pre-wrap">{note.summary || note.body}</p>
                {expandedNoteId === note.id ? (
                  <div className="mt-4 p-3 bg-gray-900/50 border border-gray-700/50 rounded-xl" onClick={(e) => e.stopPropagation()}>
                    <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-2">Edit Tags</p>
                    <TagEditor
                      tags={note.tags || []}
                      onUpdate={(newTags) => handleUpdateNoteTags(note.id, newTags)}
                      suggestions={tagSuggestions}
                      showToast={showToast}
                    />
                  </div>
                ) : (
                  (note.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(note.tags || []).map((tag) => (
                        <span key={tag} className="text-[10px] text-blue-200 bg-blue-950/60 border border-blue-800 rounded px-2 py-0.5">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {bottomControls}
    </div>
  );
}
